import type { ServiceNameType } from '@looming-tech/avani-infra-v2-shared';
import type { BarModel } from './bars.types';
import type { CellarModel } from './cellars.types';
import type { ClientModel } from './clients.types';
import type { WeekDays } from './common.types';
import type { FobModel } from './fob.types';
import type { GlassWasherModel } from './glass-washers.types';
import type { PythonModel } from './pythons.types';
import type { RemoteCoolerModel } from './remote-coolers.types';
import type { SubscriptionModel } from './subscriptions.types';
import type { UserModel } from './users.types';

export enum VenueType {
  DEFAULT = 'DEFAULT'
}

export enum DispenseSystemType {
  CHILLPOUR = 'Chillpour',
  FUTURE_OF_DISPENSE = 'Future of Dispense',
  CELLAR_PLUS = 'CellarPlus',
  STANDARD = 'Standard'
}

export enum WaterHardnessType {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH'
}

export interface VenueModel {
  id: string;
  name: string;
  address: string | null;
  town: string | null;
  county: string | null;
  country: string | null;
  postCode: string | null;
  venueDescription: string | null;
  mobilePhone: string | null;
  isPss: boolean;
  coordinatesLng: number | null;
  coordinatesLat: number | null;
  venueType: VenueType | null;
  dispenseSystem: DispenseSystemType | null;
  waterHardness: WaterHardnessType | null;
  accessDays: WeekDays[] | null;
  productDeliveryDays: WeekDays[] | null;
  accessWindowOpen: string | null;
  accessWindowClose: string | null;
  licenseOpen: string | null;
  licenseClose: string | null;
  clientId: string;
  client: ClientModel;
  bars: BarModel[];
  cellars: CellarModel[];
  remoteCoolers: RemoteCoolerModel[];
  subscriptions: SubscriptionModel[];
  glassWashers: GlassWasherModel[];
  contacts: UserModel[];
  pythons: PythonModel[];
  fobs: FobModel[];
}

export interface ListVenuesResponse {
  result: VenueListModel[];
  limit: number;
  offset: number;
  count: number;
}

export interface CreateVenueParams {
  name: string;
  clientId: string;
  contacts: string[];
  address?: string;
  town?: string;
  county?: string;
  country?: string;
  postCode?: string;
  isPss?: boolean | null;
  venueDescription?: string;
  mobilePhone?: string;
  accessWindowOpen: string | null;
  accessWindowClose: string | null;
  licenseClose: string | null;
  licenseOpen: string | null;
  coordinatesLat?: number;
  coordinatesLng?: number;
  dispenseSystem?: DispenseSystemType | null;
  waterHardness?: WaterHardnessType | null;
  productDeliveryDays?: string[];
  accessDays?: string[];
  venueType?: VenueType | null;
}

export interface UpdateVenueParams {
  name: string;
  clientId: string;
  contacts: string[];
  address?: string | null;
  town?: string | null;
  county?: string | null;
  country?: string | null;
  postCode?: string | null;
  venueDescription?: string | null;
  mobilePhone?: string | null;
  accessWindowOpen: string | null;
  accessWindowClose: string | null;
  licenseClose: string | null;
  licenseOpen: string | null;
  isPss?: boolean | null;
  coordinatesLat?: number;
  coordinatesLng?: number;
  dispenseSystem?: DispenseSystemType | null;
  waterHardness?: WaterHardnessType | null;
  productDeliveryDays?: string[] | null;
  accessDays?: string[] | null;
  venueType?: VenueType | null;
}

export interface UpdateVenueParamsWithId extends UpdateVenueParams {
  venueId: string;
  subscriptions: SubscriptionModel[];
}

export interface DeleteVenueParams {
  venueId: string;
}

export type ServicesModel = {
  serviceId: string;
  serviceName: ServiceNameType;
};

export type VenueListModel = Pick<VenueModel, 'id' | 'name' | 'town' | 'accessWindowOpen' | 'accessWindowClose'> & {
  clientName: string;
  services: ServicesModel[];
  contactNames: string[];
};

export type VenueSortingFields = 'name' | 'town' | 'clientName';

export type VenueDetailsSortingFields = 'firstName' | 'email' | 'mobilePhone' | 'lastLogin' | 'employmentStatus';

export interface VenueFiltersProps {
  name?: string;
  clientName?: string;
  town?: string;
  county?: string;
  venueType?: VenueType;
  isPss?: boolean;
  serviceId?: string;
}
